// ===== 4. Issues and Suggestions Component =====
// src/components/SoftApprovalIssues.tsx

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore,
  Error,
  Warning,
  Lightbulb,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import { SoftApprovalIssue } from '../../../../services/ai/aiSoftApprovalService';

interface SoftApprovalIssuesProps {
  criticalIssues: SoftApprovalIssue[];
  warnings: SoftApprovalIssue[];
  suggestions: SoftApprovalIssue[];
  nextSteps: string[];
  onNavigateToSection?: (section: string) => void;
}

const SoftApprovalIssues: React.FC<SoftApprovalIssuesProps> = ({
  criticalIssues,
  warnings,
  suggestions,
  nextSteps,
  onNavigateToSection
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedPanel, setExpandedPanel] = useState<string>('critical');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : '');
  };

//   const getIssueIcon = (category: string) => {
//     switch (category) {
//       case 'Critical':
//         return <Error sx={{ color: theme.palette.error.main }} />;
//       case 'Warning':
//         return <Warning sx={{ color: theme.palette.warning.main }} />;
//       case 'Suggestion':
//         return <Lightbulb sx={{ color: theme.palette.info.main }} />;
//       default:
//         return <CheckCircle sx={{ color: theme.palette.grey[500] }} />;
//     }
//   };

  const getSectionFromFieldPath = (fieldPath?: string): string => {
    if (!fieldPath) return '';
    
    // Map field paths to sections
    const sectionMap: Record<string, string> = {
      'ContractorLicenseNumber': 'Property Details',
      'AgentsLicenseNumber': 'Property Details',
      'ArchitectLicenseNumber': 'Property Details',
      'DocumentFileUpload': 'Documents',
      'PurposeType': 'General Information',
      'DescribeUse': 'General Information',
      'NumberOfUnits': 'Property Details',
      'BusinessOccupancyClassification': 'Property Details'
    };
    
    return sectionMap[fieldPath] || '';
  };

  const renderIssuesList = (issues: SoftApprovalIssue[], title: string, icon: React.ReactNode, color: string) => {
    if (issues.length === 0) return null;

    const panelId = title.toLowerCase().replace(' ', '-');

    return (
      <Accordion 
        expanded={expandedPanel === panelId} 
        onChange={handleAccordionChange(panelId)}
        sx={{ 
          mb: 1,
          border: `1px solid ${color}20`,
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            {icon}
            <Typography variant="h6" sx={{ color }}>
              {title} ({issues.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ pl: isMobile ? 0 : 4 }}>
            {issues.map((issue, index) => (
              <Alert 
                key={index}
                severity={
                  issue.category === 'Critical' ? 'error' : 
                  issue.category === 'Warning' ? 'warning' : 'info'
                }
                sx={{ mb: 2 }}
                action={
                  issue.fieldPath && onNavigateToSection ? (
                    <Button 
                      size="small" 
                      onClick={() => onNavigateToSection(getSectionFromFieldPath(issue.fieldPath))}
                      endIcon={<ArrowForward />}
                    >
                      Fix
                    </Button>
                  ) : undefined
                }
              >
                <Typography variant="subtitle2" gutterBottom>
                  {issue.title}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {issue.description}
                </Typography>
                <Box mt={1}>
                  <Chip label={issue.type} size="small" sx={{ mr: 1 }} />
                  {issue.documentType && (
                    <Chip label={issue.documentType} size="small" variant="outlined" />
                  )}
                </Box>
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Impact:</strong> {issue.impact}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    <strong>Action:</strong> {issue.action}
                  </Typography>
                </Box>
              </Alert>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (criticalIssues.length === 0 && warnings.length === 0 && suggestions.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CheckCircle sx={{ color: theme.palette.success.main }} />
            <Typography variant="h6" sx={{ color: theme.palette.success.main }}>
              No Issues Found
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Your application looks good! All sections appear complete and consistent.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Application Review Results
        </Typography>
        
        {renderIssuesList(
          criticalIssues, 
          'Critical Issues', 
          <Error />, 
          theme.palette.error.main
        )}
        
        {renderIssuesList(
          warnings, 
          'Warnings', 
          <Warning />, 
          theme.palette.warning.main
        )}
        
        {renderIssuesList(
          suggestions, 
          'Suggestions', 
          <Lightbulb />, 
          theme.palette.info.main
        )}

        {nextSteps.length > 0 && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Recommended Next Steps
            </Typography>
            <List dense>
              {nextSteps.map((step, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Typography variant="body2" sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem'
                    }}>
                      {index + 1}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={step} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SoftApprovalIssues;

//---------------------------------------------------------------------------------------------------------------------------------------------------